import { cn } from '@/shared/lib/cn';

import { Icon } from './Icon';

export interface Step {
  key: string;
  label: string;
  /** Linha de apoio abaixo do rotulo, no assistente. */
  description?: string | undefined;
  /**
   * Situacao propria da etapa no assistente: "erro" marca a etapa com campo a
   * corrigir; "concluida" marca a que ja foi preenchida, mesmo depois da atual
   * (quem volta para corrigir nao perde o que fez adiante).
   */
  state?: 'erro' | 'concluida' | undefined;
  /** Etapa que ainda nao pode ser aberta, com o motivo no titulo. */
  disabledReason?: string | undefined;
  /**
   * Etapa adiante que ainda nao abre, como na pagina da operacao antes de a esteira
   * chegar nela (ADR 0107): o marcador mostra o cadeado e o motivo vai em
   * `disabledReason`, no titulo e para o leitor de tela.
   */
  locked?: boolean | undefined;
}

export interface StepperProps {
  steps: Step[];
  currentKey: string;
  /**
   * Encerramento negativo: a progressao para onde parou. Com `onStepClick`, todas as
   * etapas abrem, para leitura: a esteira encerrada nao tem etapa adiante a esperar.
   */
  terminated?: boolean | undefined;
  /**
   * Torna as etapas navegaveis, como no assistente de cotacao, endosso e cadastro de
   * corretor. Sem ele, o indicador so mostra o andamento, como na esteira.
   *
   * So abrem as etapas ja percorridas, as marcadas como concluidas e a atual: pular
   * adiante deixaria a pessoa numa etapa que depende de dado ainda nao preenchido.
   */
  onStepClick?: ((key: string) => void) | undefined;
  /** Nome do indicador para o leitor de tela, como "Etapas da cotacao". */
  label?: string | undefined;
  /**
   * A etapa cuja tela esta aberta, quando ela pode ser outra que a atual: na pagina da
   * operacao, a atual e onde a esteira esta, e a aberta e a tela que a pessoa escolheu
   * (ADR 0107). Sem ele, nenhuma etapa se marca como aberta, como nos assistentes, em
   * que a aberta e a propria atual.
   */
  selectedKey?: string | undefined;
}

/**
 * Indicador de etapas, da esteira e dos assistentes.
 *
 * Ele responde tres perguntas de uma vez: quanto ja andou, onde esta e quanto
 * falta. Uma etiqueta com o nome da etapa atual responde apenas a segunda, e e
 * justamente a distancia ate o fim que costuma ser cobrada.
 *
 * Na esteira, a ordem e o conjunto vem do servidor, e nao de uma lista mantida aqui:
 * uma etapa nova aparece sem alteracao no cliente. No assistente, as etapas sao do
 * formulario, e `useStepper` guarda em qual a pessoa esta.
 */
export function Stepper({
  steps,
  currentKey,
  terminated,
  onStepClick,
  label,
  selectedKey,
}: StepperProps) {
  const currentIndex = steps.findIndex((step) => step.key === currentKey);

  return (
    <ol
      aria-label={label}
      className="rolagem-fina flex w-full items-start gap-1 overflow-x-auto pb-1"
    >
      {steps.map((step, index) => {
        const percorrida = currentIndex >= 0 && index < currentIndex;
        const concluida =
          step.state === 'concluida' || (percorrida && step.state !== 'erro');
        const atual = index === currentIndex;
        const erro = step.state === 'erro';
        const travada = step.locked === true;
        const aberta = selectedKey !== undefined && step.key === selectedKey;
        const navegavel =
          onStepClick !== undefined &&
          !step.disabledReason &&
          !travada &&
          (percorrida || concluida || atual || terminated === true);

        const marcador = (
          <span
            aria-hidden
            className={cn(
              'flex size-6 shrink-0 items-center justify-center rounded-full',
              'text-2xs font-semibold ring-1 transition-[background-color,color,box-shadow]',
              'duration-(--duration-base)',
              // Feita: verde de sucesso, com o visto branco (3,8:1 sobre o 600).
              concluida &&
                !atual &&
                'bg-success-600 bg-linear-to-br from-success-600 to-success-700 text-white ring-success-600',
              // A atual: cheia na cor da empresa, com o anel de halo. E onde a pessoa esta.
              atual &&
                !terminated &&
                !erro &&
                'bg-brand-500 text-white ring-brand-500 shadow-[0_0_0_4px_color-mix(in_oklab,var(--color-brand-500)_20%,transparent)]',
              ((atual && terminated) || erro) &&
                'bg-danger-50 text-danger-700 ring-danger-500 shadow-[0_0_0_4px_color-mix(in_oklab,var(--color-danger-500)_14%,transparent)]',
              // As futuras: discretas, mas com o numero legivel (4,7:1).
              !concluida &&
                !atual &&
                !erro &&
                'bg-surface text-ink-500 ring-line-strong',
            )}
          >
            {erro ? (
              <Icon name="alert" size={12} />
            ) : concluida && !atual ? (
              <Icon name="check" size={12} />
            ) : travada && !atual ? (
              <Icon name="lock" size={12} />
            ) : (
              index + 1
            )}
          </span>
        );

        const texto = (
          <>
            {/* O nome inteiro aparece: quebra em ate duas linhas entre palavras, em vez
                de ser cortado no meio ("Aguardando cred..."). O titulo repete o nome. */}
            <span
              className={cn(
                'line-clamp-2 max-w-24 text-center text-2xs leading-tight text-balance',
                erro
                  ? 'font-medium text-danger-700'
                  : atual
                    ? 'font-semibold text-brand-700'
                    : concluida
                      ? 'font-medium text-ink-700'
                      : 'text-ink-500',
              )}
              title={travada ? step.label : (step.disabledReason ?? step.label)}
            >
              {step.label}
            </span>
            {step.description ? (
              <span className="max-w-28 truncate text-center text-2xs text-ink-500">
                {step.description}
              </span>
            ) : null}
            {erro ? <span className="sr-only">com campo a corrigir</span> : null}
            {concluida && !atual ? <span className="sr-only">concluida</span> : null}
            {travada ? (
              <span className="sr-only">
                travada{step.disabledReason ? `: ${step.disabledReason}` : ''}
              </span>
            ) : null}
          </>
        );

        return (
          <li
            key={step.key}
            aria-current={atual ? 'step' : undefined}
            className="flex min-w-0 flex-1 items-start gap-1"
          >
            {navegavel ? (
              <button
                type="button"
                onClick={() => onStepClick(step.key)}
                // A tela aberta se anuncia como o item atual do conjunto; a etapa em
                // que a esteira esta continua com aria-current="step", no item.
                aria-current={aberta ? 'true' : undefined}
                className={cn(
                  'flex min-w-0 flex-col items-center gap-1.5 rounded-md p-0.5',
                  'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500',
                  // Na pagina da operacao cada etapa e uma tela: o realce no passar do
                  // ponteiro diz que ela abre. Os assistentes ficam como estavam.
                  selectedKey !== undefined &&
                    'cursor-pointer transition-colors duration-(--duration-fast) hover:bg-brand-50/70',
                  aberta && 'bg-brand-50 ring-1 ring-brand-200',
                )}
              >
                {marcador}
                {texto}
              </button>
            ) : (
              <div
                className={cn(
                  'flex min-w-0 flex-col items-center gap-1.5 rounded-md p-0.5',
                  aberta && 'bg-brand-50 ring-1 ring-brand-200',
                  travada && 'cursor-not-allowed',
                )}
                title={travada ? step.disabledReason : undefined}
              >
                {marcador}
                {texto}
              </div>
            )}

            {index < steps.length - 1 ? (
              <span
                aria-hidden
                className={cn(
                  // Na altura do centro do marcador (p-0.5 mais metade de size-6), com
                  // qualquer numero de linhas no nome da etapa.
                  'mt-[13px] h-0.5 flex-1 rounded-full transition-colors duration-(--duration-base)',
                  percorrida ? 'bg-success-500' : 'bg-line-strong',
                )}
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}
